import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation, Link } from 'react-router-dom';

// const API_BASE_URL = 'http://localhost:8080'; 
const API_BASE_URL = 'https://sardar-jewellers.com'; 

interface Address {
    addressId?: number;
    userSrno: number;
    fullName: string;
    mobileNo: string;
    streetAddress: string;
    city: string;
    state: string;
    pincode: string;
    addressType: string;
}

// --- Custom Notification Component ---
const Notification: React.FC<{ message: string, type: 'success' | 'error', onClose: () => void }> = ({ message, type, onClose }) => (
    <div className={`mb-6 p-4 rounded-lg flex justify-between items-center ${type === 'success' ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
        <span className="font-medium">{message}</span>
        <button onClick={onClose} className="text-xl font-bold opacity-70 hover:opacity-100">&times;</button>
    </div>
);

// --- 1. Profile Details Component ---
const ProfileContent: React.FC<{ user: any }> = ({ user }) => {
    const { login } = useAuth(); 
    const [name, setName] = useState(user.userName || '');
    const [mobile, setMobile] = useState(user.mobileNo || '');
    const [isUpdating, setIsUpdating] = useState(false);
    const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);

    const handleUpdateProfile = async () => {
        setIsUpdating(true);
        setNotification(null);
        try {
            const response = await fetch(`${API_BASE_URL}/api/user-profile/update`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ srNo: user.srNo, userName: name, mobileNo: mobile }),
            });

            const data = await response.json();
            if (response.ok && data.success) {
                login({ ...user, userName: name, mobileNo: mobile });
                setNotification({ message: 'Your profile has been successfully updated.', type: 'success' });
            } else {
                setNotification({ message: data.message || 'Failed to update profile.', type: 'error' });
            }
        } catch (error) {
            setNotification({ message: 'A network error occurred while updating your profile.', type: 'error' });
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <h3 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b">My Profile Details</h3>
            {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">Full Name</label>
                    <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none" />
                </div>
                <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-600">Email ID (Read Only)</label>
                    <input type="email" value={user.emailId || ""} readOnly className="w-full px-4 py-3 border border-gray-200 bg-gray-100 rounded-lg text-gray-500 cursor-not-allowed" />
                </div>
                <div className="space-y-1.5">
                    <label className="text-sm font-medium text-gray-700">Mobile Number</label>
                    <input type="tel" value={mobile} onChange={(e) => setMobile(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 outline-none" maxLength={10} />
                </div>
            </div>
            <button onClick={handleUpdateProfile} disabled={isUpdating} className="mt-8 px-10 py-3 bg-amber-700 text-white font-semibold rounded-lg shadow-sm hover:bg-amber-800 transition disabled:bg-gray-400">
                {isUpdating ? 'Saving Changes...' : 'Save Changes'}
            </button>
        </div>
    );
};

// --- 2. Manage Addresses Component ---
const AddressContent: React.FC<{ user: any }> = ({ user }) => {
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [showForm, setShowForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);
    const [formData, setFormData] = useState({ fullName: '', mobileNo: '', streetAddress: '', city: '', state: '', pincode: '', addressType: 'Home' });

    const fetchAddresses = async () => {
        setIsLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/user-profile/address/${user.srNo}`);
            if (res.ok) {
                const data = await res.json();
                setAddresses(data);
            }
        } catch (error) { console.error("Failed to fetch addresses", error); } 
        finally { setIsLoading(false); }
    };

    useEffect(() => { fetchAddresses(); }, [user.srNo]);

    const handleSaveAddress = async () => {
        setNotification(null);
        if(!formData.streetAddress || !formData.city || !formData.pincode) {
            setNotification({ message: 'Please fill all required fields.', type: 'error' });
            return;
        }
        try {
            const newAddress: Address = { ...formData, userSrno: user.srNo };
            const response = await fetch(`${API_BASE_URL}/api/user-profile/address/add`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(newAddress),
            });
            if (response.ok) {
                setNotification({ message: 'New address has been saved successfully.', type: 'success' });
                setShowForm(false);
                setFormData({ fullName: '', mobileNo: '', streetAddress: '', city: '', state: '', pincode: '', addressType: 'Home' }); 
                fetchAddresses(); 
            } else { setNotification({ message: 'Failed to save the address.', type: 'error' }); }
        } catch (error) { setNotification({ message: 'Network error occurred.', type: 'error' }); }
    };

    return (
        <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <div className="flex justify-between items-center mb-6 pb-4 border-b">
                <h3 className="text-xl font-bold text-gray-900">Manage Addresses</h3>
                {!showForm && ( <button onClick={() => setShowForm(true)} className="px-5 py-2.5 bg-amber-50 text-amber-700 font-semibold rounded-lg border border-amber-200 hover:bg-amber-100 transition">+ Add New Address</button> )}
            </div>
            {notification && <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />}
            
            {showForm && (
                <div className="mb-8 p-6 border border-gray-200 rounded-xl bg-gray-50">
                    <h4 className="font-semibold text-gray-800 mb-4">Add New Address</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <input type="text" placeholder="Full Name" className="p-3 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} />
                        <input type="tel" placeholder="Mobile No" className="p-3 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" value={formData.mobileNo} onChange={e => setFormData({...formData, mobileNo: e.target.value})} />
                        <input type="text" placeholder="Pincode" className="p-3 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" value={formData.pincode} onChange={e => setFormData({...formData, pincode: e.target.value})} />
                        <input type="text" placeholder="City" className="p-3 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" value={formData.city} onChange={e => setFormData({...formData, city: e.target.value})} />
                        <input type="text" placeholder="State" className="p-3 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" value={formData.state} onChange={e => setFormData({...formData, state: e.target.value})} />
                        <select className="p-3 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" value={formData.addressType} onChange={e => setFormData({...formData, addressType: e.target.value})}>
                            <option value="Home">Home</option>
                            <option value="Office">Office</option>
                        </select>
                        <div className="md:col-span-2">
                            <textarea placeholder="Street Address / Area" rows={2} className="w-full p-3 border rounded-lg outline-none focus:ring-1 focus:ring-amber-500" value={formData.streetAddress} onChange={e => setFormData({...formData, streetAddress: e.target.value})}></textarea>
                        </div>
                    </div>
                    <div className="flex gap-3">
                        <button onClick={handleSaveAddress} className="px-6 py-2 bg-amber-700 text-white rounded-lg hover:bg-amber-800 transition">Save Address</button>
                        <button onClick={() => setShowForm(false)} className="px-6 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition">Cancel</button>
                    </div>
                </div>
            )}

            {isLoading ? ( <div className="text-gray-600">Loading your addresses...</div> ) : addresses.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {addresses.map((addr) => (
                        <div key={addr.addressId} className="p-5 border border-gray-200 rounded-xl relative group hover:border-amber-300 transition">
                            <span className={`absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full ${addr.addressType === 'Home' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>{addr.addressType?.toUpperCase() || 'HOME'}</span>
                            <p className="font-semibold text-gray-900 text-lg">{addr.fullName}</p>
                            <p className="text-sm text-gray-600 mt-2">{addr.streetAddress}</p>
                            <p className="text-sm text-gray-600">{addr.city}, {addr.state} - <span className='font-medium text-gray-800'>{addr.pincode}</span></p>
                            <p className="text-sm text-gray-600 mt-2">Phone: <span className="font-medium text-gray-800">{addr.mobileNo}</span></p>
                        </div>
                    ))}
                </div>
            ) : ( <div className="text-center py-10 border border-gray-200 rounded-xl"><p className="text-gray-500">No addresses saved yet.</p></div> )}
        </div>
    );
};

// --- 3. LIVE ORDER COMPONENT (Updated with details & Cancel feature) ---
const OrderContent: React.FC<{ user: any }> = ({ user }) => {
    const [orders, setOrders] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const fetchOrders = async () => {
            if (!user) return;
            try {
                const response = await fetch(`${API_BASE_URL}/api/orders/user/${user.srNo}`);
                if (response.ok) {
                    const data = await response.json();
                    setOrders(data);
                }
            } catch (error) {
                console.error("Network error while fetching orders", error);
            } finally {
                setIsLoading(false);
            }
        };
        fetchOrders();
    }, [user]);

    // NAYA CODE: Order Cancel karne ka function
    const handleCancelOrder = async (orderId: number) => {
        const confirmCancel = window.confirm("Are you sure you want to cancel this order?");
        if (!confirmCancel) return;

        try {
            // Tumhari purani API use ho rahi hai status update ke liye
            const response = await fetch(`${API_BASE_URL}/api/orders/update-status/${orderId}?status=CANCELLED`, {
                method: 'PUT',
            });

            if (response.ok) {
                // UI me turant update karne ke liye state change kar do
                setOrders(prevOrders => 
                    prevOrders.map(order => 
                        order.orderId === orderId ? { ...order, status: 'CANCELLED' } : order
                    )
                );
                alert("Order cancelled successfully.");
            } else {
                alert("Failed to cancel the order. Please try again.");
            }
        } catch (error) {
            console.error("Error cancelling order", error);
            alert("Network error while cancelling order.");
        }
    };

    if (isLoading) return <div className="p-10 text-center text-lg font-medium text-gray-600">Loading your orders...</div>;

    return (
        <div className="p-6 bg-white border border-gray-100 rounded-2xl shadow-sm min-h-[60vh]">
            <h3 className="text-xl font-bold text-gray-900 mb-6 pb-4 border-b">My Orders</h3>
            {orders.length === 0 ? (
                <div className="text-center py-16 flex flex-col items-center">
                    <img src="https://cdn-icons-png.flaticon.com/512/2762/2762885.png" alt="No orders" className="w-32 h-32 mb-6 opacity-70 grayscale" />
                    <h4 className="text-xl font-semibold text-gray-800 mb-2">You haven't placed any orders yet!</h4>
                    <p className="text-gray-500 mb-6">Looks like you haven't made your choice yet.</p>
                    <Link to="/" className="px-6 py-3 bg-amber-700 text-white font-semibold rounded-lg hover:bg-amber-800 transition">Start Shopping</Link>
                </div>
            ) : (
                <div className="space-y-6">
                    {orders.map((order) => (
                        <div key={order.orderId} className="border border-gray-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow bg-white">
                            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
                                <div><p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Order ID</p><p className="font-bold text-gray-900">#{order.orderId}</p></div>
                                <div><p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Date</p><p className="font-medium text-gray-800">{new Date(order.orderDate).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</p></div>
                                <div><p className="text-xs text-green-600 font-semibold uppercase tracking-wider mb-1">Expected Delivery</p><p className="font-medium text-gray-800">{new Date(order.estimatedDelivery).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}</p></div>
                                <div><p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Total Amount</p><p className="font-bold text-amber-800">₹{order.totalAmount.toFixed(2)}</p></div>
                                
                                <div className="flex items-center gap-3">
                                    {/* Status Badge */}
                                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                        order.status === 'PLACED' ? 'bg-blue-100 text-blue-700' : 
                                        order.status === 'DELIVERED' ? 'bg-green-100 text-green-700' : 
                                        order.status === 'CANCELLED' ? 'bg-red-100 text-red-700' :
                                        'bg-gray-200 text-gray-800'
                                    }`}>{order.status}</span>
                                    
                                    {/* NAYA CODE: Cancel Button (Sirf tab dikhega jab order PLACED ho) */}
                                    {order.status === 'PLACED' && (
                                        <button 
                                            onClick={() => handleCancelOrder(order.orderId)}
                                            className="text-xs font-bold text-red-600 border border-red-600 px-3 py-1 rounded-full hover:bg-red-50 hover:text-red-700 transition"
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </div>
                            
                            <div className="p-6">
                                {order.items && order.items.length > 0 ? (
                                    <ul className="divide-y divide-gray-100">
                                        {order.items.map((item: any, index: number) => (
                                            <li key={index} className="py-4 flex justify-between items-center">
                                                <div className="flex items-center gap-5">
                                                    {/* NAYA CODE: Image ko thoda bada aur sundar kiya */}
                                                    <div className="w-16 h-16 bg-gray-50 border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0">
                                                        {item.productImage ? (
                                                            <img src={item.productImage} alt={item.productTitle} className="w-full h-full object-cover" />
                                                        ) : (
                                                            <span className="text-2xl opacity-50">📦</span>
                                                        )}
                                                    </div>
                                                    
                                                    {/* NAYA CODE: Product ka title aur Quantity dikhane ke liye */}
                                                    <div>
                                                        <p className="font-semibold text-gray-800 text-lg">{item.productTitle || "Jewellery Item"}</p>
                                                        <p className="text-sm text-gray-500 font-medium">Qty: {item.quantity}</p>
                                                    </div>
                                                </div>
                                                
                                                <p className="font-bold text-gray-900 text-lg">₹{(item.price * item.quantity).toFixed(2)}</p>
                                            </li>
                                        ))}
                                    </ul>
                                ) : <p className="text-sm text-gray-500 italic">Processing details...</p>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// --- 4. Placeholder Components (For Wishlist/Wallet) ---
const ComingSoonContent: React.FC<{ title: string }> = ({ title }) => (
    <div className="p-10 bg-white border border-gray-100 rounded-2xl shadow-sm min-h-[50vh] flex flex-col items-center justify-center text-center">
        <h3 className="text-2xl font-bold text-gray-800 mb-2">{title}</h3>
        <p className="text-gray-500">This feature is currently under development. Stay tuned!</p>
    </div>
);

// --- Main Page Component ---
const UserProfilePage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    useEffect(() => { if (!user) navigate('/login'); }, [user, navigate]);
    if (!user) return null;

    const currentPath = location.pathname;
    
    const navLinks = [
        { name: 'Profile Details', path: '/profile' },
        { name: 'Manage Addresses', path: '/addresses' }, 
        { name: 'My Orders', path: '/orders' },
        { name: 'Wishlist', path: '/wishlist' },
        { name: 'Wallet', path: '/wallet' }
    ];
    
    const renderContent = () => {
        if (currentPath.startsWith('/addresses')) return <AddressContent user={user} />;
        if (currentPath.startsWith('/orders')) return <OrderContent user={user} />; // Ab yeh naya wala chalega!
        if (currentPath.startsWith('/wishlist')) return <ComingSoonContent title="My Wishlist" />; 
        if (currentPath.startsWith('/wallet')) return <ComingSoonContent title="My Wallet" />;
        return <ProfileContent user={user} />; 
    };
    
    return (
        <main className="min-h-screen bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
                <h1 className="text-3xl font-extrabold text-gray-900 mb-10 tracking-tight">
                    Welcome back, <span className='text-amber-700'>{user.userName}</span> 👋
                </h1>
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Sidebar Navigation */}
                    <aside className="lg:w-1/4 w-full shrink-0">
                        <nav className="flex lg:flex-col overflow-x-auto gap-1.5 pb-2 bg-white lg:bg-transparent p-2 rounded-2xl border lg:border-none border-gray-100">
                            {navLinks.map((link) => {
                                const isActive = currentPath === link.path || (currentPath === '/profile' && link.path === '/profile');
                                return (
                                    <Link 
                                        key={link.name} 
                                        to={link.path} 
                                        className={`whitespace-nowrap px-5 py-3.5 rounded-xl text-sm font-semibold transition-all ${isActive ? 'bg-amber-50 text-amber-800 lg:border-l-4 lg:border-amber-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                    >
                                        {link.name}
                                    </Link>
                                );
                            })}
                        </nav>
                    </aside>
                    
                    {/* Content Area */}
                    <section className="lg:w-3/4 w-full">
                        {renderContent()} 
                    </section>
                </div>
            </div>
        </main>
    );
};

export default UserProfilePage;